/**
 * Migración: campo legacy `context` → nueva jerarquía (entorno, tipo).
 *
 * Aplica a cualquier `Partial<Transaction>` cargado desde Firestore, LocalStorage
 * o snapshots antiguos. La función es idempotente: si el registro ya trae
 * `entorno`/`tipo` válidos, los respeta.
 *
 * Mapeo:
 *  - context='Personal'   → entorno='Personal', tipo='Saldo'
 *  - context='Empresa'    → entorno='Empresa',  tipo='Saldo'
 *  - context='Inversión'  → entorno='Personal', tipo='Inversión'
 *  - context='Compartida' → entorno='Personal', tipo='Saldo'
 *    (la dimensión "Compartida" es ahora una propiedad de la Cuenta, no del
 *    movimiento; el prorrateo se aplica en KPIs vía Account.ownershipPercentage)
 */
import type {
    Bucket,
    Entorno,
    TipoMovimiento,
} from '../../types';
import { getTipoFromCategory } from '../taxonomy';

export interface ClassificationTriple {
    entorno: Entorno;
    tipo: TipoMovimiento;
}

export interface ClassificationPair {
    entorno: Entorno;
    tipo: TipoMovimiento;
}

/**
 * Shape mínimo que necesita el helper para derivar la clasificación.
 * Más permisivo que `Partial<Transaction>` para evitar problemas de
 * distribución de tipos sobre discriminated unions.
 */
export interface ClassificationInput {
    entorno?: Entorno;
    tipo?: TipoMovimiento;
    context?: string;
    bucket?: Bucket;
    amount?: number;
    relationshipType?: string;
    category?: string;
    assetId?: string;
}

export function deriveClassification(raw: ClassificationInput): ClassificationPair {
    // 1. Determinar tipo (Saldo / Inversión / Traspaso)
    let tipo: TipoMovimiento;
    // La categoría V3 MANDA sobre el tipo almacenado: cada categoría pertenece a un
    // único tipo (salvo 'Préstamo', que se desambigua con tipo/activo). Sin esta
    // prioridad, un movimiento categorizado como Inversión/Traspaso por el pipeline de
    // importación —que sólo escribe category/bucket— se quedaba con el tipo 'Saldo'
    // que traía por defecto, y la ficha mostraba "Sin Categorizar" al filtrar por tipo.
    const tipoFromCategory = getTipoFromCategory(raw.category, {
        tipo: raw.tipo,
        hasAsset: !!raw.assetId,
    });
    if (tipoFromCategory) {
        tipo = tipoFromCategory;
    } else if (raw.tipo === 'Saldo' || raw.tipo === 'Inversión' || raw.tipo === 'Traspaso') {
        tipo = raw.tipo;
    } else if (raw.bucket === 'TRASPASO' || raw.relationshipType === 'transfer') {
        tipo = 'Traspaso';
    } else if (raw.context === 'Inversión' || raw.bucket === 'INVERSION') {
        tipo = 'Inversión';
    } else {
        tipo = 'Saldo';
    }

    // 2. Determinar entorno
    let entorno: Entorno;
    if (raw.entorno === 'Personal' || raw.entorno === 'Empresa') {
        entorno = raw.entorno;
    } else if (raw.context === 'Empresa') {
        entorno = 'Empresa';
    } else {
        entorno = 'Personal';
    }

    return { entorno, tipo };
}

/**
 * Mapa de subcategoría legacy de la antigua cat 'Inversión' al nuevo contexto+categoría.
 * Orden: categoría del tipo Inversión V3 (sin contexto).
 */
const LEGACY_INVERSION_SUB_TO_CAT: Record<string, string> = {
    'Dividendos':     'Dividendos',
    'Alquiler Cobrado':'Alquiler Cobrado',
    'Adquisición':    'Adquisición',
    'Reformas':       'Reinversión',
    'Reinversión':    'Reinversión',
    'Aportación':     'Adquisición',
    'Reembolso':      'Retiro',
    'Venta':          'Retiro',
    'Comisiones':     'Comisiones',
    // Finding #7 FULL_AUDIT (import-pipeline lot): 'Hipoteca' was mapped to
    // 'Impuestos Activo' (mortgage counted as asset taxes), contradicting D1.
    // The V3 category for a loan installment under tipo='Inversión' is 'Préstamo'
    // (sub 'Hipoteca' remains valid there).
    'Hipoteca':       'Préstamo',
    'Suministros':    'Suministros',
    'Comunidad':      'Comunidad',
    'Reparaciones':   'Reparaciones',
    'Seguros':        'Seguros Activo',
    'Impuestos':      'Impuestos Activo',
};

const isLegacyInversionCategory = (cat: string | undefined | null): boolean =>
    !!cat && cat === 'Inversión';

/**
 * Aplica un patch parcial a una transacción y re-deriva la clasificación
 * coherente (entorno, tipo) basándose en la unión de señales del
 * patch, las existentes en `tx` y las pistas de categoría/bucket.
 */
export function applyClassification<T extends ClassificationInput & { category?: string; subCategory?: string }>(
    tx: T,
    patch: (ClassificationInput & { category?: string; subCategory?: string }) | undefined = undefined,
): T & ClassificationPair {
    const merged = { ...tx, ...(patch ?? {}) } as T & ClassificationInput & { category?: string; subCategory?: string };

    const finalCategory = (patch as { category?: string } | undefined)?.category ?? (tx as { category?: string }).category;
    const finalSubCategory = (patch as { subCategory?: string } | undefined)?.subCategory ?? (tx as { subCategory?: string }).subCategory;

    if (finalCategory) {
        if (isLegacyInversionCategory(finalCategory)) {
            // Migración: antigua categoría 'Inversión' → nuevo tipo/categoría según sub
            merged.tipo = 'Inversión';
            const mappingCat = finalSubCategory ? LEGACY_INVERSION_SUB_TO_CAT[finalSubCategory] : undefined;
            if (mappingCat) {
                (merged as Record<string, unknown>).category = mappingCat;
            } else {
                (merged as Record<string, unknown>).category = 'Adquisición'; // default fallback
            }
        } else if (finalCategory === 'Compensaciones' && finalSubCategory === 'Aportación Cuenta Común') {
            // Aportación Cuenta Común → Traspaso/Movimiento Interno
            merged.tipo = 'Traspaso';
            (merged as Record<string, unknown>).category = 'Movimiento Interno';
        } else if (finalCategory === 'Compensaciones' || finalCategory === 'Devoluciones') {
            // Categorías legacy (unificadas en 'Ajustes' en V3): fuera de la taxonomía
            // actual, así que su tipo no es derivable — se fija aquí.
            merged.tipo = 'Saldo';
        }
        // El resto de categorías V3 no necesita rama: deriveClassification obtiene el
        // tipo de la propia categoría (getTipoFromCategory), que es la fuente de verdad.
    }

    const pair = deriveClassification(merged);
    return { ...merged, ...pair } as T & ClassificationPair;
}
